'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { isCapacitorApp } from '@/shared/libs/platform/is-capacitor-app';
import { fetcherWithToken } from '@/shared/api/fetcher';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { UpdatePopup } from '../ui/popups/update-popup';
import { useBrandTheme } from '@/shared/hooks/useBrandTheme';
import { downloadFile } from '@/shared/libs/download-file';
import { getAppFileName } from '@/shared/libs/get-app-file-name';
import {
  getLatestBuild,
  isAppUpdateRequired,
  isSupportedNativePlatform,
} from '@/shared/libs/app-version';
import type { AppVersions, NativePlatform } from '@/shared/libs/app-version';

const TWA_SESSION_KEY = 'is_twa_session';
const APK_VERSION_CHECKED_KEY = 'apk_version_checked';
const APK_UPDATE_REQUIRED_KEY = 'apk_update_required';
const APK_UPDATE_DISMISSED_KEY = 'apk_update_dismissed';

let appUpdateCheckPromise: Promise<boolean> | null = null;

const getIsAppUpdateRequired = async (
  platform: NativePlatform
): Promise<boolean> => {
  try {
    const [versionsResponse, appInfo] = await Promise.all([
      fetcherWithToken(
        `/appVersion?app=${process.env.NEXT_PUBLIC_TARGET}`
      ) as Promise<AppVersions>,
      App.getInfo(),
    ]);

    return isAppUpdateRequired(
      appInfo.build,
      getLatestBuild(versionsResponse, platform)
    );
  } catch {
    return false;
  }
};

export function getIsTwaSession(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.sessionStorage.getItem(TWA_SESSION_KEY) === 'true';
}

export const TwaInitializer = () => {
  const searchParams = useSearchParams();
  const { assets, currentBrand } = useBrandTheme();
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);

  useEffect(() => {
    if (!isCapacitorApp()) {
      return;
    }

    const platform = Capacitor.getPlatform();
    const classNames = ['capacitor-app', `capacitor-${platform}`];

    document.body.classList.add(...classNames);

    return () => {
      document.body.classList.remove(...classNames);
    };
  }, []);
  useEffect(() => {
    if (searchParams.get('source') === 'twa') {
      window.sessionStorage.setItem(TWA_SESSION_KEY, 'true');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isCapacitorApp()) {
      return;
    }

    const platform = Capacitor.getPlatform();
    if (!isSupportedNativePlatform(platform)) {
      return;
    }

    const wasChecked =
      window.sessionStorage.getItem(APK_VERSION_CHECKED_KEY) === 'true';

    if (wasChecked && appUpdateCheckPromise === null) {
      setShowUpdatePopup(
        window.sessionStorage.getItem(APK_UPDATE_REQUIRED_KEY) === 'true' &&
          window.sessionStorage.getItem(APK_UPDATE_DISMISSED_KEY) !== 'true'
      );
      return;
    }

    window.sessionStorage.setItem(APK_VERSION_CHECKED_KEY, 'true');
    appUpdateCheckPromise ??= getIsAppUpdateRequired(platform);

    let isMounted = true;

    void appUpdateCheckPromise.then((needUpdate) => {
      window.sessionStorage.setItem(
        APK_UPDATE_REQUIRED_KEY,
        String(needUpdate)
      );

      if (
        isMounted &&
        window.sessionStorage.getItem(APK_UPDATE_DISMISSED_KEY) !== 'true'
      ) {
        setShowUpdatePopup(needUpdate);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const isAnotherModalOpen = searchParams.has('modal');

  if (!showUpdatePopup || isAnotherModalOpen) {
    return null;
  }

  const handleClose = () => {
    window.sessionStorage.setItem(APK_UPDATE_DISMISSED_KEY, 'true');
    setShowUpdatePopup(false);
  };

  const platform = Capacitor.getPlatform();
  const downloadUrl = isSupportedNativePlatform(platform)
    ? assets.appLink?.[platform]
    : undefined;

  const handleReinstall = async () => {
    if (!isSupportedNativePlatform(platform)) {
      return;
    }

    const result = await downloadFile(
      downloadUrl,
      getAppFileName(currentBrand, platform)
    );

    if (result.success) {
      handleClose();
    }
  };

  return (
    <UpdatePopup
      onClose={handleClose}
      onReinstall={handleReinstall}
      reinstallDisabled={!downloadUrl}
    />
  );
};
